import { breakpoints } from "@indeed/ifl-tokens";
import React, { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Button, Dialog, Pressable } from "../../../mads";
import { Box, type BoxRef } from "../../../mads/Box";
import { DialogSize } from "../../../mads/Dialog";
import { Heading } from "../../../mads/Heading";
import { ArrowLeft } from "../../../mads/icons/ArrowLeft";
import { useTheme, useThemedStylesheet } from "../../../mads/ThemeProvider";
import { createThemedStylesheet } from "../../../shared/util/createThemedStylesheet";
import { type TranslatedString, useI18n } from "../../I18n";
import { useEscapeKey, useModalFocusTrap } from "../../util/useWebModalAccessibility";

const HEADER_SIDE_WIDTH = 44;

// Inlined from former apps/profile/util/layout.ts (PROFILE_MODAL_MAX_WIDTH).
// The combobox modal caps its width on wider viewports so it doesn't stretch
// edge-to-edge on desktop web/tablet.
const COMBOBOX_MODAL_MAX_WIDTH = 600;

export type ModalTemplateVariant = "fill" | "fit" | "fullScreen";

export type ModalTemplateProps = {
  /** Animation type (ignored on web; accepted for API parity with native) */
  animationType?: "none" | "slide" | "fade";
  /** Modal body content */
  children?: ReactNode;
  /** Whether there are unsaved changes; shows a discard confirmation on close if true */
  isDirty?: boolean;
  /** Called when the modal is dismissed (back button or backdrop tap) */
  onClose: () => void;
  /** Called immediately when modal dismissal begins. */
  onCloseStart?: () => void;
  /** Called when the Done button is pressed */
  onDone: () => void;
  doneButtonRef?: React.ComponentProps<typeof Button>["ref"];
  /** Called after the modal is presented. */
  onShow?: () => void;
  /** Orientations the modal may rotate to on iOS (ignored on web; accepted for API parity with native) */
  supportedOrientations?: Array<
    "portrait" | "portrait-upside-down" | "landscape" | "landscape-left" | "landscape-right"
  >;
  /** Title displayed in the modal header */
  title: TranslatedString;
  /** Native sizing variant; ignored on web where viewport width determines presentation */
  variant?: ModalTemplateVariant;
  /** Whether the modal is visible. When false the parent should unmount or skip rendering. */
  visible?: boolean;
};

/**
 * Tracks whether the viewport matches the desktop breakpoint.
 * Listens for resize changes and updates reactively.
 */
function useDesktopMediaQuery() {
  const mediaQueryRef = useRef(window.matchMedia(`screen and (min-width: ${breakpoints.sm})`));
  const [isDesktop, setIsDesktop] = useState(mediaQueryRef.current.matches);

  useEffect(() => {
    const mediaQuery = mediaQueryRef.current;
    const handleChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return isDesktop;
}

export const ModalTemplate = ({
  children,
  isDirty,
  onClose,
  onCloseStart,
  onDone,
  doneButtonRef,
  onShow,
  title,
  visible,
}: ModalTemplateProps) => {
  const theme = useTheme();
  const { gettext } = useI18n();
  const styles = useThemedStylesheet(stylesheet);
  const titleId = `combobox-modal-title-${useId()}`;
  const dialogRef = useRef<BoxRef>(null);
  const isNarrow = !useDesktopMediaQuery();
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);
  const wasVisibleRef = useRef(false);
  const closeStartSignaledRef = useRef(false);
  const onCloseStartRef = useRef(onCloseStart);
  const onShowRef = useRef(onShow);
  onCloseStartRef.current = onCloseStart;
  onShowRef.current = onShow;

  const signalCloseStart = useCallback(() => {
    if (closeStartSignaledRef.current) {
      return;
    }
    closeStartSignaledRef.current = true;
    onCloseStartRef.current?.();
  }, []);

  const [portalEl] = useState<HTMLElement>(() => {
    const el = document.createElement("div");
    el.setAttribute("role", "presentation");
    return el;
  });
  useEffect(() => {
    document.body.appendChild(portalEl);
    return () => {
      document.body.removeChild(portalEl);
    };
  }, [portalEl]);

  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      closeStartSignaledRef.current = false;
      onShowRef.current?.();
    } else if (!visible && wasVisibleRef.current) {
      signalCloseStart();
    }
    wasVisibleRef.current = !!visible;
  }, [signalCloseStart, visible]);

  useModalFocusTrap(dialogRef, !!visible);

  const handleClose = () => {
    if (isDirty) {
      setShowSaveChangesDialog(true);
      return;
    }
    signalCloseStart();
    onClose();
  };

  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  useEscapeKey(handleCloseRef, !!visible);

  const dismissSaveChangesDialog = () => setShowSaveChangesDialog(false);

  const handleSave = () => {
    dismissSaveChangesDialog();
    signalCloseStart();
    onDone();
  };

  const handleDiscard = () => {
    dismissSaveChangesDialog();
    signalCloseStart();
    onClose();
  };

  const handleDone = () => {
    signalCloseStart();
    onDone();
  };

  if (!visible) return null;

  const content = (
    <Box sx={[styles.overlay, isNarrow && styles.overlayNarrow]}>
      <Pressable sx={styles.backdrop} onPress={handleClose} accessible={false} />
      <Box
        ref={dialogRef}
        sx={[styles.container, isNarrow ? styles.containerSheet : styles.containerDialog]}
        role="dialog"
        aria-modal={true}
        aria-labelledby={titleId}
        focusable
      >
        <Dialog
          visible={showSaveChangesDialog}
          onClose={dismissSaveChangesDialog}
          size={DialogSize.sm}
          portaled={true}
          title={gettext("Save changes")}
          subtitle={gettext("Would you like to save your changes before leaving?")}
          buttons={[
            {
              variant: "primary",
              onPress: handleSave,
              children: gettext("Save changes"),
              size: "md",
            },
            {
              variant: "destructiveSecondary",
              onPress: handleDiscard,
              children: gettext("Discard changes"),
              size: "md",
            },
          ]}
        />
        <Box sx={styles.header} accessibilityRole="header">
          <Box sx={styles.headerBefore}>
            <Button
              ariaLabel={gettext("Close")}
              onPress={handleClose}
              variant="tertiary"
              iconBefore={<ArrowLeft size="sm" color={theme.colors.foregroundStrong} />}
              iconOnly
            />
          </Box>
          <Box sx={styles.headerTitle}>
            <Heading level={3} nativeID={titleId} sx={styles.titleHeading}>
              {title}
            </Heading>
          </Box>
          <Box sx={styles.headerAfter} />
        </Box>
        <Box sx={styles.body}>{children}</Box>
        <Box sx={styles.footer}>
          <Button onPress={handleDone} ref={doneButtonRef}>
            {gettext("Done")}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return createPortal(content, portalEl);
};

const stylesheet = createThemedStylesheet((theme) => ({
  overlay: {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    position: "fixed" as never,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10000,
  },
  overlayNarrow: {
    justifyContent: "flex-end",
    alignItems: "stretch",
  },
  backdrop: {
    position: "absolute",
    inset: 0,
    backgroundColor: theme.colors.utilityOverlay,
  },
  container: {
    backgroundColor: theme.colors.background,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    width: "100%" as never,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    overflow: "hidden" as never,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    display: "flex" as never,
    flexDirection: "column",
  },
  body: {
    flex: 1,
    minHeight: 0,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    overflowY: "auto" as never,
    padding: theme.spacing.s4,
  },
  footer: {
    flexShrink: 0,
    padding: theme.spacing.s4,
    borderTopWidth: 1,
    borderTopColor: theme.colors.borderWeak,
    backgroundColor: theme.colors.background,
    gap: theme.spacing.s3,
  },
  containerDialog: {
    borderRadius: theme.borderRadius.lg,
    maxWidth: COMBOBOX_MODAL_MAX_WIDTH,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    maxHeight: "90%" as never,
  },
  containerSheet: {
    borderTopLeftRadius: theme.borderRadius.lg,
    borderTopRightRadius: theme.borderRadius.lg,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    maxHeight: "95%" as never,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.s2,
    padding: theme.spacing.s3,
    minHeight: 44,
  },
  headerBefore: {
    width: HEADER_SIDE_WIDTH,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  headerTitle: {
    flex: 1,
  },
  titleHeading: {
    width: "100%",
    textAlign: "center",
  },
  headerAfter: {
    width: HEADER_SIDE_WIDTH,
  },
}));
